import Dexie, { type Table } from "dexie";

export type NodeType = "experience" | "project" | "lens" | "home" | "graph" | "print";

export interface VisitRecord {
  id?: number;
  ts: number;
  path: string;
  title?: string;
  nodeType: NodeType;
  nodeId?: string;
  filters?: string;
}

export interface BookmarkRecord {
  id?: number;
  ts: number;
  nodeType: NodeType;
  nodeId: string;
  title: string;
  note?: string;
}

export type QuestionStatus = "draft" | "submitted" | "ai-draft" | "answered" | "error";

export interface QuestionRecord {
  id?: number;
  ts: number;
  nodeType: NodeType;
  nodeId: string;
  nodeTitle: string;
  question: string;
  contactEmail?: string;
  contactName?: string;
  status: QuestionStatus;
  serverId?: string;
  answer?: string;
  answerTs?: number;
  answeredBy?: string;
}

export interface MetaRecord {
  id: "meta";
  visitorUuid: string;
  contactName?: string;
  contactEmail?: string;
  animations?: boolean;
  journeyLimit?: number;
  theme?: "light" | "dark"; // legacy: the visitor's resolved theme (migrated to themePref on read)
  themePref?: "light" | "dark"; // the visitor's EXPLICIT choice; absent = follow the admin default
  graphState?: GraphState;
  triedFeatures?: string[];
}

export interface GraphState {
  // Entity ids (without "exp:"/"proj:" prefix) whose children are revealed.
  // Empty expanded sets + no viewBy = Level-1 view (Experience bubbles only).
  expandedExperiences: string[];
  expandedProjects: string[];
  // When set, switches the graph to a "view by" context — the tag is the
  // root, and all experiences/projects carrying it are revealed.
  viewBy?: { type: "industry" | "technology" | "skill" | "role"; id: string };
  // Currently-selected bubble for the side panel.
  selected?: { type: string; id: string };
}

class ResumeDB extends Dexie {
  visits!: Table<VisitRecord, number>;
  bookmarks!: Table<BookmarkRecord, number>;
  questions!: Table<QuestionRecord, number>;
  meta!: Table<MetaRecord, string>;

  constructor() {
    super("interactive-resume");
    this.version(1).stores({
      visits: "++id, ts, nodeType, nodeId",
      bookmarks: "++id, ts, &[nodeType+nodeId]",
      questions: "++id, ts, nodeType, nodeId, status",
      meta: "&id",
    });
  }
}

export const db = new ResumeDB();

export async function ensureVisitorUuid(): Promise<string> {
  const existing = await db.meta.get("meta");
  if (existing?.visitorUuid) return existing.visitorUuid;
  const uuid =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `v-${Math.random().toString(36).slice(2)}-${Date.now()}`;
  await db.meta.put({ id: "meta", visitorUuid: uuid });
  return uuid;
}

export async function getMeta(): Promise<MetaRecord | undefined> {
  return db.meta.get("meta");
}

export async function setContact(name: string, email: string) {
  const existing = (await db.meta.get("meta")) ?? {
    id: "meta" as const,
    visitorUuid: await ensureVisitorUuid(),
  };
  await db.meta.put({ ...existing, contactName: name, contactEmail: email });
}
